import { useEffect, useRef } from "react";
import type { KeyState } from "../wordle/keystate";

/** What one split-flap module shows: a glyph and its flap colour (null = plain board). */
export interface Face {
  ch: string;
  s: KeyState | null;
}

export type FlapMode = "type" | "flip" | "none";

interface FlapTileProps {
  face: Face;
  /**
   * How a change of `face` plays: "type" rattles through random letters before
   * landing, "flip" is one clean flip (reveals), "none" snaps.
   */
  mode: FlapMode;
  /** ms before the change starts — the reveal stagger, or a strip's letter-by-letter roll. */
  delay?: number;
  /** Fires when a "flip" lands (haptic tick). */
  onLand?: () => void;
  /** Bump to replay a flip-through to the same face (the win wave). */
  wave?: number;
  waveDelay?: number;
  /** Letters the flip-through rattles through. */
  alphabet?: string;
  className?: string;
}

const REDUCED = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));
const same = (a: Face, b: Face) => a.ch === b.ch && a.s === b.s;

function paint(el: HTMLElement | null, f: Face): void {
  if (!el) return;
  el.dataset.s = f.s ?? "";
  const glyph = el.firstElementChild;
  if (glyph) glyph.textContent = f.ch;
}

/**
 * One split-flap module. Four layers: the static top half already shows the
 * next face, the static bottom still shows the old one; the top leaf (old)
 * falls toward the viewer, then the bottom leaf (new) lands over the old
 * bottom. Driven imperatively (Web Animations) so React renders never cut a
 * flip in half; changes queue and a newer target skips stale steps.
 */
export function FlapTile({ face, mode, delay = 0, onLand, wave = 0, waveDelay = 0, alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ", className = "" }: FlapTileProps) {
  const top = useRef<HTMLDivElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const leafTop = useRef<HTMLDivElement>(null);
  const leafBottom = useRef<HTMLDivElement>(null);
  const shown = useRef<Face>(face);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const gen = useRef(0);
  const lastWave = useRef(wave);

  const flipOnce = async (to: Face, ms: number): Promise<void> => {
    const from = shown.current;
    const lt = leafTop.current;
    const lb = leafBottom.current;
    if (!lt || !lb || REDUCED) {
      paint(top.current, to);
      paint(bottom.current, to);
      shown.current = to;
      return;
    }
    paint(top.current, to);
    paint(bottom.current, from);
    paint(lt, from);
    paint(lb, to);
    lt.style.visibility = "visible";
    lb.style.visibility = "visible";
    const half = ms / 2;
    const fall = lt.animate(
      [
        { transform: "rotateX(0deg)", filter: "brightness(1)" },
        { transform: "rotateX(-90deg)", filter: "brightness(0.5)" },
      ],
      { duration: half, easing: "cubic-bezier(.55,0,.9,.45)", fill: "forwards" },
    );
    const land = lb.animate(
      [
        { transform: "rotateX(90deg)", filter: "brightness(0.7)" },
        { transform: "rotateX(0deg)", filter: "brightness(1)" },
      ],
      { duration: half * 1.3, delay: half, easing: "cubic-bezier(.25,1.3,.55,1)", fill: "both" },
    );
    try {
      await land.finished;
    } catch {
      // cancelled by unmount — fall through to the final paint
    }
    paint(bottom.current, to);
    fall.cancel();
    land.cancel();
    lt.style.visibility = "hidden";
    lb.style.visibility = "hidden";
    shown.current = to;
  };

  /** `supersede` = a new target: stale queued steps skip. A wave only rides along. */
  const enqueue = (steps: (alive: () => boolean) => Promise<void>, supersede = true) => {
    const my = supersede ? ++gen.current : gen.current;
    const alive = () => gen.current === my;
    chain.current = chain.current.then(() => (alive() ? steps(alive) : undefined)).catch(() => undefined);
  };

  const randomLetter = (not: string) => {
    let ch = not;
    while (ch === not) ch = alphabet[Math.floor(Math.random() * alphabet.length)] ?? "A";
    return ch;
  };

  // First paint: no animation, whatever the face is (restored games show their grid at rest).
  useEffect(() => {
    paint(top.current, shown.current);
    paint(bottom.current, shown.current);
  }, []);

  useEffect(() => {
    const target: Face = { ch: face.ch, s: face.s };
    if (same(shown.current, target)) return;
    if (mode === "none") {
      gen.current++;
      paint(top.current, target);
      paint(bottom.current, target);
      shown.current = target;
      return;
    }
    if (mode === "flip") {
      enqueue(async (alive) => {
        if (delay > 0) await sleep(delay);
        if (!alive()) return;
        await flipOnce(target, 320);
        onLand?.();
      });
      return;
    }
    // "type": a letter lands after rattling through two random ones; clearing is one flip.
    enqueue(async (alive) => {
      if (delay > 0) await sleep(delay);
      if (target.ch !== "") {
        for (const ms of [70, 70]) {
          if (!alive()) return;
          await flipOnce({ ch: randomLetter(target.ch), s: target.s }, ms);
        }
      }
      if (alive()) await flipOnce(target, 110);
    });
  }, [face.ch, face.s]);

  useEffect(() => {
    if (wave === lastWave.current) return;
    lastWave.current = wave;
    enqueue(async (alive) => {
      await sleep(waveDelay);
      const at = shown.current;
      if (!alive()) return;
      await flipOnce({ ch: randomLetter(at.ch), s: at.s }, 110);
      if (alive()) await flipOnce(at, 150);
    }, false);
  }, [wave]);

  return (
    <div className={`flap ${className}`}>
      <div ref={top} className="flap-h fh-top">
        <span />
      </div>
      <div ref={bottom} className="flap-h fh-bot">
        <span />
      </div>
      <div ref={leafTop} className="flap-h fh-top flap-leaf" style={{ visibility: "hidden" }}>
        <span />
      </div>
      <div ref={leafBottom} className="flap-h fh-bot flap-leaf flap-leaf-bot" style={{ visibility: "hidden" }}>
        <span />
      </div>
    </div>
  );
}
